import { setTag } from '@sentry/nextjs';
import { getGlobalContentfulData } from 'api/contentful';
import { config } from 'config';
import { useConfig } from 'config/use-config';
import { Entry } from 'contentful';
import {
  ICrossSellPopUpDataFields,
  IGlobalData,
  IGlobalDataFields,
  INavigation,
  IPromoBar,
} from 'global';
import useDynamicBundleDiscountHeaders from 'hooks/common/use-dynamic-bundle-discount-headers';
import useGlobalDiscount, { GlobalDiscount } from 'hooks/common/use-global-discount';
import { useRouter } from 'next/router';
import { useRegion } from 'hooks/use-region';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDebounce } from 'react-use';
import { useCookieStore } from 'stores/cookie';
import useSWR from 'swr';
import { Gtm } from 'tracking';
import { getContentfulGlobalDataLocale } from 'utils/i18n/get-contentful-locale';
import { globalData as fallbackData } from 'utils/static-global-data';

/**  Product input type for Impressions*/
export interface ImpressionTrackProductType {
  productId: string | number;
  productType: string;
  productTitle: string;
  tags: Array<string>;
  /** Position in slider */
  position: number;
  /** Collection List name */
  list: string;
}

interface CommonContextType {
  crossSellPopUpData?: ICrossSellPopUpDataFields;
  globalData?: Entry<IGlobalDataFields>;
  globalDiscount: GlobalDiscount;
  mainPromoBar: IPromoBar | undefined;
  navigation: INavigation | undefined;
  productImpressionsQueue: Array<ImpressionTrackProductType>;
  trackedImpressionStorage: { [key in string]: Array<string | number> };
  setProductImpressionsQueue: React.Dispatch<React.SetStateAction<ImpressionTrackProductType[]>>;
  setTrackedImpressionStorage: React.Dispatch<
    React.SetStateAction<{ [key in string]: Array<string | number> }>
  >;
}

const CommonContext = createContext<CommonContextType>({} as any);

export const CommonContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { branchName } = config;
  const { globalDataEntryId } = useConfig();
  const { locale: activeLocale } = useIntl();
  const { cookies } = useCookieStore();
  if (!fallbackData) {
    console.error('static global data is missing!');
  }

  /**
   * Tracks impression per page
   * @see useImpressionTracking
   */
  const [trackedImpressionStorage, setTrackedImpressionStorage] = useState<{
    [key in string]: Array<string | number>;
  }>({});

  /**
   * Tracks product impressions per page
   */
  const [productImpressionsQueue, setProductImpressionsQueue] = useState<
    Array<ImpressionTrackProductType>
  >([]);

  /**
   * Track product impressions
   * @see ProductImpressionsTracker
   */
  useDebounce(
    () => {
      if (productImpressionsQueue.length > 0) {
        const newMap = productImpressionsQueue.map((product) => ({
          productId: product.productId + '',
          productType: product.productType,
          productTitle: product.productTitle,
          tags: product.tags.join(','),
          position: product.position,
          list: product.list,
        }));

        Gtm.track({
          group: Gtm.Group.Product,
          name: Gtm.ProductEvent.Impressions,
          data: { products: newMap },
          nonInteractive: true,
        });
        setProductImpressionsQueue([]);
      }
    },
    500,
    [productImpressionsQueue]
  );

  const router = useRouter();
  const region = useRegion();

  /**
   * Reset impression store when page routes to another path
   * so that impression is tracked per page
   * */
  useEffect(() => {
    const handleRouteChange = (url, { shallow }): void => {
      // reset only when deep page transition, meaning excluding page params update
      if (!shallow) {
        setTrackedImpressionStorage({});
        setProductImpressionsQueue([]);
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router, setTrackedImpressionStorage]);

  const globalDataCacheKey = `global-${region}-${activeLocale}`;

  const { data: globalData } = useSWR(
    globalDataCacheKey,
    () =>
      getGlobalContentfulData({
        globalDataEntryId,
        locale: activeLocale,
      }),
    {
      fallback: {
        [globalDataCacheKey]: fallbackData[getContentfulGlobalDataLocale(activeLocale)],
      },
      revalidateOnFocus: false,
    }
  );

  const crossSellPopUpData = globalData?.fields.crossSellPopUp?.fields;

  const globalDiscount = useGlobalDiscount(globalData as IGlobalData);

  useDynamicBundleDiscountHeaders(globalData);

  const mainPromoBar = useMemo(() => globalData?.fields.mainPromoBar, [globalData]);

  const navigation = useMemo(() => globalData?.fields.navigation, [globalData]);

  // Set release version tag for Sentry
  useEffect(() => {
    if (cookies?.pd_rv) setTag('pd_release_version', cookies.pd_rv);
  }, [cookies?.pd_rv]);

  // Set branch name tag for Sentry
  useEffect(() => {
    setTag('branchName', branchName);
  }, [branchName]);

  const data = useMemo(() => {
    return {
      crossSellPopUpData,
      globalData,
      globalDiscount,
      mainPromoBar,
      navigation,
      productImpressionsQueue,
      trackedImpressionStorage,
      setProductImpressionsQueue,
      setTrackedImpressionStorage,
    };
  }, [
    crossSellPopUpData,
    globalData,
    globalDiscount,
    mainPromoBar,
    navigation,
    productImpressionsQueue,
    trackedImpressionStorage,
    setProductImpressionsQueue,
    setTrackedImpressionStorage,
  ]);

  return <CommonContext.Provider value={data}>{children}</CommonContext.Provider>;
};

export const useCommon = (): CommonContextType => useContext(CommonContext);
